/** @jsx jsx */
import { css, jsx } from '@emotion/core'
import React, { useState } from 'react'
import { Accordion } from 'antd-mobile'
import { PICK_ESTATE, PICK_TRACK_TYPE, PICK_TYPE, PickTrack } from '@store/pickStore'
import {
  formatSimpleNumber,
  getPickPublishMoney,
  schema$PickMatchInfoToSchema$ComponentBettingParamMatches,
} from '@utils/utils'
import dateFormat from 'dateformat'
import { ArrDayText } from '@game/util'
import useRootStore from '@store/useRootStore'

import { MoreBtnIco, PickMessageIco, PickOffIco, PickOnIco } from '@shared/Icon/PickIcon'

// icon
import { GmoneyIco } from '@shared/Icon/MoneyIcon'
import { CircleDownIco } from '@shared/Icon/ArrowIcon'
import DummyImg from '@assets/images/itemshop/skin_dummy.png'
// css
import styled, { Theme } from '@themes/theme'
import { Point, TxtLimit } from '@styles/base.style'
import { Button } from '@styles/button.style'
import { BET_HIT_STATE, PROPERTY_ID } from 'adv-protocol/src/GameCommon'
import { useOnMount } from '@utils/reactExt'
import MessageContentPopup from '@views/Popup/MessageContentPopup'
import { PUBLISH_PICK_TYPE } from '@constants/index'
import { ComponentBettingParams } from '@store/componentStore'
import {
  AccordionListWrap,
  BettingButtonStyle,
  ButtonWrap,
  CircleDownStyle,
  Hit,
  IconButton,
  Miss,
  PickHiddenWrap,
  PickType,
  PickWrap,
  SettingStyle,
  SpecialCase,
} from './index.style'
import { FROM_VIEW_TYPE } from '@v2/constants/common'

const Arrow = (
  <CircleDownStyle className="arrow">
    <CircleDownIco/>
  </CircleDownStyle>
)

const Gmoney = <GmoneyIco/>

const HitStyle = (theme: Theme) =>
  css`
      color: ${theme.color.blue};
      border: 1px solid ${theme.color.blue};
  `
const StandbyStyle = (theme: Theme) =>
  css`
      color: ${theme.color.themeTxt};
      border: 1px solid ${theme.color.themeTxt};
  `
const MissStyle = (theme: Theme) =>
  css`
      color: ${theme.color.red};
      border: 1px solid ${theme.color.red};
  `
const SpecialCaseStyle = (theme: Theme) =>
  css`
      color: ${theme.color.purple};
      border: 1px solid ${theme.color.purple};
  `

const CollapseWrap = styled.span``

// indexed by PICK_TRACK_TYPE
const PICK_TYPE_ELEMENT = [
  undefined,
  <PickType key="pick-list-item-my-pick">마이픽</PickType>,
  <PickType key="pick-list-item-pick-buy-done">구매완료</PickType>,
  <PickType key="pick-list-item-pick-standby" css={StandbyStyle}>대기</PickType>,
  <PickType key="pick-list-item-pick-hit" css={HitStyle}>적중</PickType>,
  <PickType key="pick-list-item-pick-fail" css={MissStyle}>미적중</PickType>,
  <PickType key="pick-list-item-pick-cancel" css={SpecialCaseStyle}>적중특례</PickType>,
]

// image
const Dummy = <img key="dummy" src={DummyImg} alt="프로필"/>

interface PickListItemProps {
  currentIndex: number
  track: PickTrack
  trackType: PICK_TRACK_TYPE
}

const PickListItem: React.FC<PickListItemProps> = (props: PickListItemProps) => {
  const { currentIndex, track, trackType } = props
  const {
    pickId,
    type,
    title,
    picksterName,
    userHitRatioTotal,
    foldCount,
    dividendRate,
    priceMoney,
    estate,
    hitState,
    sell,
    dateBettingEndInst,
    matches = [],
    picksterImg,
    clubId: pickClubId,
    isSpecificClub: isSpecificClubPick,
    publishMoneyType,
  } = track

  const prefix = 'https://d1i7hydgfclv7m.cloudfront.net/data_center/item_image/'
  const [visiblePopup, setVisiblePopup] = useState(false)
  const [selling, setSelling] = useState(sell)
  const [collapsed, setCollapsed] = useState(true)
  const dateText = dateFormat(dateBettingEndInst, 'mm/dd')
  const timeText = dateFormat(dateBettingEndInst, 'HH:MM')
  const dayText = ArrDayText[dateBettingEndInst.getDay()]
  const dateOfMatchText = `${dateText}(${dayText}) ${timeText}`
  // 베팅마감 8/20(화) 21:55

  const { pickStore, authStore, bettingHistoryStore, componentStore } = useRootStore()
  const { clubId: userClubId, isSpecificClubMember } = authStore.userInfo
  const [updator, setUpdator] = useState(0)
  const pickPrice = getPickPublishMoney(publishMoneyType, priceMoney)

  const update = () => {
    setUpdator(updator + 1)
  }

  useOnMount(() => {
    pickStore.addViewController(currentIndex, pickId, update)
  })

  const onClickShowDrawer = e => {
    // e.stopPropagation()
    if (estate === PICK_ESTATE.NONE) {
      pickStore.setPurchasePick(track)
    }
  }

  const onClickShowMessageDrawer = () => {
    // setVisibleMessageModal(true)
    bettingHistoryStore.resetSelectedBettingHistory(FROM_VIEW_TYPE.USER_PICK)
    bettingHistoryStore.setBettingHistoryInitProps({ onlyMajorClub: true, fromViewType: FROM_VIEW_TYPE.USER_PICK })
    setVisiblePopup(true)
  }

  const toggleSaleField = async () => {
    const ret = await pickStore.controlPick(pickId, selling === 1 ? 0 : 1, publishMoneyType)
    setSelling(ret)
  }

  const now = new Date()
  let betAvailable = hitState === BET_HIT_STATE.ONGOING && now.valueOf() < dateBettingEndInst.valueOf()

  if (betAvailable) {
    if (isSpecificClubPick) {
      if (!isSpecificClubMember || userClubId !== pickClubId) {
        betAvailable = false
      }
    } else if (!isSpecificClubPick) {
      if (isSpecificClubMember) {
        betAvailable = false
      }
    }
  }

  let eleMyPick = null

  const onClickToggleCollapse = () => setCollapsed(!collapsed)
  const onClickBettingButton = async (e) => {
    e.stopPropagation()
    const pick = pickStore.getBettingPick(pickId)
    if (pick) {
      componentStore.addCartItemsFromPickMatches(pick.matches)
      const result = await componentStore.validateClub(pick.clubId)
      if (result) {
        const { club } = await componentStore.api.club.getClubInfo({ clubId: pick.clubId })
        const componentBettingParams: ComponentBettingParams = {
          club,
          typeOn: pick.typeOn,
          callType: pick.callType,
          matches: schema$PickMatchInfoToSchema$ComponentBettingParamMatches(pick.matches),
          betMoney: 0,
          betMoneyType: PROPERTY_ID.MONEY,
          pickId,
          bettorBetId: pick.bettorBetId,
        }
        componentStore.setComponentBettingParams(componentBettingParams, true)
      }
    }
  }

  if (betAvailable && estate === PICK_ESTATE.PUBLISHED) {
    const PickMessage = (
      <IconButton onClick={onClickShowMessageDrawer} className="icon-btn">
        <PickMessageIco/>
      </IconButton>
    )

    const Sale = (
      <IconButton onClick={toggleSaleField} className="icon-btn">
        {selling === 0 ? <PickOffIco/> : <PickOnIco/>}
      </IconButton>
    )
    eleMyPick = (
      <ButtonWrap
        onClick={e => {
          e.stopPropagation()
        }}
      >
        <CollapseWrap css={SettingStyle}>
          <CollapseWrap className={collapsed ? 'collapse' : 'expand'} onClick={onClickToggleCollapse}>
            <MoreBtnIco/>
          </CollapseWrap>
          <span className="setting-wrap">
            {PickMessage}
            {Sale}
          </span>
        </CollapseWrap>
      </ButtonWrap>
    )
  }

  let eleBtnBet = null
  const eleMatches = []
  let isArrow = true
  let count = 1
  if (type === PICK_TYPE.MARKET && estate === PICK_ESTATE.NONE) {
    isArrow = false
  }
  if (type !== PICK_TYPE.MARKET || (type === PICK_TYPE.MARKET && estate !== PICK_ESTATE.NONE)) {
    if (type === PICK_TYPE.PURCHASE && betAvailable === true) {
      eleBtnBet = (
        <Button secondary css={BettingButtonStyle} onClick={onClickBettingButton}>
          베팅
        </Button>
      )
    }
    for (const el of matches) {
      const eleOdds = []
      const dateOfMatch = new Date(el.dateOfMatch)
      const dateTxt = dateFormat(dateOfMatch, 'mm/dd')
      const timeTxt = dateFormat(dateOfMatch, 'HH:MM')
      const dayTxt = ArrDayText[new Date(dateOfMatch).getDay()]
      const dateOfMatchTxt = `${dateTxt}(${dayTxt}) ${timeTxt}`
      const eleScore =
        hitState === BET_HIT_STATE.ONGOING ? (
          <span className="pick-match-info--team--score">VS</span>
        ) : (
          <span className="pick-match-info--team--score team-score">
              <span className="pick-match-info--team--score--num win">{el.homeTeamScore}</span>:
            <span className="pick-match-info--team--score--num lose">{el.awayTeamScore}</span>
            </span>
        )

      for (const fold of el.folds) {
        let eleHitStyle
        switch (fold.hitState) {
          case BET_HIT_STATE.SUCCESS:
            eleHitStyle = <span css={Hit}>적중</span>
            break
          case BET_HIT_STATE.FAIL:
            eleHitStyle = <span css={Miss}>미적중</span>
            break
          case BET_HIT_STATE.CANCEL:
            eleHitStyle = <span css={SpecialCase}>적중특례</span>
            break
        }
        eleOdds.push(
          <div key={`pickListItem_odds_${count}`} className="fold-wrap--bottom">
            <span className="fold-wrap--bottom--market-name">{fold.marketName}</span>
            <ul className="fold-wrap--bottom--select-result">
              <li className="txt">선택</li>
              <li className="hyphen">-</li>
              <li className="type">{fold.selectionName}</li>
              <li className="txt" style={{ marginLeft: 10 }}>결과</li>
              <li className="hyphen">-</li>
              <li className="type">{fold.outcomeName}</li>
              <li className="bet-hit-state" style={{ marginLeft: 8 }}>{eleHitStyle}</li>
            </ul>
          </div>
          ,
        )
        count += 1
      }
      eleMatches.push(
        <PickHiddenWrap key={`pickListItem_match_${count}`}>
          <h4 className="league-name">
            {el.sportName} - {el.tournamentName}
          </h4>

          <div className="pick-match-info">
            <span className="pick-match-info--date">{dateOfMatchTxt}</span>
            <div className="pick-match-info--team">
              <span className="pick-match-info--team--name" css={TxtLimit}>{el.homeTeamName}</span>
              {eleScore}
              <span className="pick-match-info--team--name" css={TxtLimit}>{el.awayTeamName}</span>
            </div>
          </div>

          <div className="fold-wrap">
            {eleOdds}
          </div>

        </PickHiddenWrap>,
      )
      count += 1
    }
  }
  let picksterIco = Dummy
  if (picksterImg) {
    picksterIco = <img src={`${prefix}${picksterImg}.png`} alt="프로필"/>
  }
  return (
    <AccordionListWrap>
      <Accordion>
        <Accordion.Panel
          header={
            <PickWrap onClick={onClickShowDrawer}>
              {eleBtnBet}
              {eleMyPick}
              {PICK_TYPE_ELEMENT[trackType]}
              <span className="pick-date">{dateOfMatchText}</span>
              {isArrow && Arrow}
              <h3 className="pick-list-tit">{title}</h3>

              <div className="pick-list">
                <span className="pick-list--pickster-info">
                  <span className="pick-list--pickster-info--pickster-img">{picksterIco}</span>
                  <span className="pick-list--pickster-info--pickster">
                    <span className="pick-list--pickster-info--pickster--name">{picksterName}</span>
                    <span className="pick-list--pickster-info--pickster--hit-rate">
                      적중률
                    </span>
                    <span className="pick-list--pickster-info--pickster--percent">{userHitRatioTotal}%</span>
                  </span>
                </span>

                <span className="pick-list--pick-info">
                  <span className="pick-list--pick-info--sub">
                    <span className="pick-list--pick-info--sub--txt">폴드</span>
                    <span className="pick-list--pick-info--sub--num">{foldCount}</span>
                  </span>
                  <span className="pick-list--pick-info--sub">
                    <span className="pick-list--pick-info--sub--txt">배당</span>
                    <Point>{dividendRate.toFixed(2)}</Point>
                  </span>
                  <span className="pick-list--pick-info--sub price">
                    <span className="pick-list--pick-info--sub--txt">판매가</span>
                    <span className="pick-list--pick-info--sub--money-wrap">
                      <span className="pick-list--pick-info--sub--money-wrap--money" css={TxtLimit}>{formatSimpleNumber(pickPrice)}</span>
                      <span className="pick-list--pick-info--sub--money-wrap--icon">
                        {publishMoneyType === PUBLISH_PICK_TYPE.HEART ? Gmoney : '개'}
                      </span>
                    </span>
                  </span>
                </span>
              </div>
            </PickWrap>
          }
          key={pickId}
        >
          {eleMatches}
        </Accordion.Panel>
      </Accordion>
      <MessageContentPopup visible={visiblePopup} setVisible={setVisiblePopup} targetPick={track}
                           fromViewType={FROM_VIEW_TYPE.USER_PICK}/>
    </AccordionListWrap>
  )
}

export default PickListItem
